import { v4 as uuidv4 } from "uuid";
import { format } from "date-fns";
import type { PerfEval } from "./forms/formTypes";
import type { Scenario } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "./scenarioCalculations/calculationTypes";
import { convert } from "@/libs/units";
import { GasFlowRateUnits } from "@/libs/units/flowRate";
import type { ErpWellReadings } from "gql/api";
import { Override_Options_Enum } from "gql/api";

export default (
  input: ErpWellReadings | null,
  scenario: Scenario,
  scenarioCalculations: ScenarioCalculations
): PerfEval | null => {
  if (!input) return null;
  const performanceEvaluationId = uuidv4();
  const res: PerfEval = {
    __typename: "performance_evaluations",
    id: performanceEvaluationId,
    winning_revision_id: null,
    scenario_id: scenario.id ?? null,
    // Should never have a parent if created from wells
    parent_id: null,
    title: `Generated Performance Evaluation - ${format(new Date(), "MMMM-dd-yyyy hh:mm")}`,
    pump_intake_pressure_psia:
      input.pump_intake_pressure ??
      scenarioCalculations.pumpIntakePressurePSIA.value,
    pump_hz: input.motor_frequency ?? scenario.parameters?.pump_hz,
    gas_rate_mscf_day:
      input.produced_gas_rate ??
      convert(
        scenarioCalculations.surfaceFlowRates.value.gasFlowRateSCFD,
        GasFlowRateUnits.CubicFeetPerDay,
        GasFlowRateUnits.ThousandCubicFeetPerDay
      ),
    oil_rate_std_barrel_day:
      input.produced_oil_rate ??
      scenarioCalculations.surfaceFlowRates.value.oilFlowRateSTBD,
    water_rate_std_barrel_day:
      input.produced_water_rate ??
      scenarioCalculations.surfaceFlowRates.value.waterFlowRateSTBD,
    casing_pressure_psia:
      input.casing_pressure ?? scenario.parameters?.bottom_casing_pressure_psia,
    tubing_pressure_psia:
      input.tubing_pressure ?? scenario.parameters?.bottom_tubing_pressure_psia,
    intake_override_gas_separation_percent:
      scenario.parameters?.intake_override_gas_separation_percent,
    natural_override_gas_separation_percent:
      scenario.parameters?.natural_override_gas_separation_percent,
    intake_gas_separation_override_type:
      scenario.parameters?.intake_gas_separation_override_type,
    natural_gas_separation_override_type: Override_Options_Enum.Calculate,
    r_xref_pumps:
      scenario.equipment?.r_xref_pumps.map(xrefPump => ({
        __typename: "performance_evaluations_xref_pumps",
        id: uuidv4(),
        winning_revision_id: null,
        performance_evaluation_id: performanceEvaluationId,
        xref_pump_id: xrefPump.id,
        brake_horsepower_correction_ratio:
          xrefPump.brake_horsepower_correction_ratio,
        head_correction_ratio: xrefPump.head_correction_ratio,
        rate_correction_ratio: xrefPump.rate_correction_ratio
      })) || []
  };

  return res;
};
